'use client';

import { useGetUser } from '@/hooks/useAuth';
import { usePiAccessStatus } from '@/hooks/usePiAccess';
import PrimeAccessModal from '@/app-components/shared/prime-access';
import { Loader2, Clock, RefreshCcw } from 'lucide-react';
import { useEffect } from 'react';
import { useLoading } from '@/contexts/LoadingContext';

interface PrimeAccessGuardProps {
  children: React.ReactNode;
}

/**
 * PrimeAccessGuard - Ensures user has activated Prime Access membership
 *
 * Flow:
 * - Checks PiAccess status via API endpoint
 * - If hasAccess is false: Show activation/purchase modal
 * - ASETRACLUB tier users: Free activation (price = 0)
 * - GENERAL tier users: Must purchase (price > 0)
 * - After activation/purchase: Allow access (verified via backend)
 *
 * Note: This checks actual payment status via backend API.
 * The backend validates payment via blockchain transactions and user membership tier.
 */
export function PrimeAccessGuard({ children }: PrimeAccessGuardProps) {
  const { data: user, refetch: refetchUser, isLoading: isUserLoading } = useGetUser();
  const {
    data: piAccessStatus,
    refetch: refetchStatus,
    isLoading: isStatusLoading,
  } = usePiAccessStatus();
  const { addLoadingStep, removeLoadingStep } = useLoading();

  // Manage loading state — combine both flags into one so the effect only
  // re-runs when the overall loading state actually changes (true→false),
  // not when one query finishes while the other is still in flight.
  const isLoading = isUserLoading || isStatusLoading;

  useEffect(() => {
    if (isLoading) {
      addLoadingStep('prime-access', 'Memverifikasi akses Prime Member...');
      return () => {
        removeLoadingStep('prime-access');
      };
    } else {
      removeLoadingStep('prime-access');
    }
  }, [isLoading, addLoadingStep, removeLoadingStep]);

  const handleClose = () => {
    // Refetch both user data and PiAccess status
    refetchUser();
    refetchStatus();
  };

  const handleSuccess = () => {
    // Refetch to get latest status
    refetchUser();
    refetchStatus();
  };

  // If still loading, render children (loading screen is shown by LoadingContext)
  if (isLoading) {
    return <>{children}</>;
  }

  if (!user) {
    return <>{children}</>;
  }

  if (!piAccessStatus?.piAccess) {
    return <PrimeAccessModal onClose={handleClose} onSuccess={handleSuccess} />;
  }

  if (piAccessStatus?.piAccess?.status === 'INACTIVE') {
    return (
      <div className='fixed inset-0 z-50 flex items-center justify-center bg-linear-to-br from-amber-50 via-orange-50 to-yellow-50'>
        <div className='max-w-md mx-4 text-center'>
          {/* Animated Icon */}
          <div className='relative mb-6'>
            <div className='absolute inset-0 flex items-center justify-center'>
              <div className='w-24 h-24 bg-amber-200 rounded-full animate-ping opacity-20'></div>
            </div>
            <div className='relative flex items-center justify-center'>
              <Clock className='w-16 h-16 text-amber-600 animate-pulse' />
            </div>
          </div>

          {/* Main Message */}
          <h2 className='text-2xl font-bold text-gray-800 mb-3'>Menunggu Konfirmasi Pembayaran</h2>

          <p className='text-gray-600 mb-2 leading-relaxed'>
            Sistem kami sedang memverifikasi pembayaran Anda di blockchain.
          </p>

          <p className='text-sm text-gray-500 mb-6'>
            Proses ini biasanya memakan waktu beberapa menit. Mohon bersabar dan jangan tutup
            halaman ini.
          </p>

          {/* Status Info Box */}
          <div className='bg-white/80 backdrop-blur-sm rounded-lg p-4 mb-6 border border-amber-200'>
            <div className='flex items-start gap-3 text-left'>
              <Loader2 className='w-5 h-5 text-amber-600 animate-spin shrink-0 mt-0.5' />
              <div className='flex-1'>
                <p className='text-sm font-medium text-gray-700 mb-1'>
                  Status: Pending Verification
                </p>
                <p className='text-xs text-gray-500'>
                  Kami sedang memeriksa transaksi Anda untuk konfirmasi blockchain
                </p>
              </div>
            </div>
          </div>

          {/* Refresh Button */}
          <button
            onClick={handleSuccess}
            className='inline-flex items-center gap-2 px-6 py-3 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg'
          >
            <RefreshCcw className='w-4 h-4' />
            Cek Status Terbaru
          </button>

          {/* Helper Text */}
          <p className='text-xs text-gray-400 mt-6'>
            Halaman ini akan otomatis diperbarui setelah pembayaran dikonfirmasi
          </p>
        </div>
      </div>
    );
  }

  // User has access, render children
  return <>{children}</>;
}
